import { useState } from "react";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useLayout } from "../../hooks/useWindowWidth";
import { useThemeContext } from "../../theme/ThemeContext";
import { overlayBackdrop } from "../../theme/styles";
import { RADIUS } from "../../theme/tokens";
import { Button } from "../shared";
import { dbUpdateEntry } from "../../db";
import { appendRemark, REMARK_PATTERN } from "../../utils/entryUtils";
import { useSwipeToClose } from "../../hooks/useSwipeToClose";

export function PartnerRemarkModal({ entry, airline, partnerName, date, onClose, onSave }) {
  const { SA, GLASS, TYPOGRAPHY } = useThemeContext();
  useLockBodyScroll();
  useEscapeKey(onClose);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const { isWide } = useLayout();
  const { handleProps, dragStyle } = useSwipeToClose(onClose, { enabled: !isWide });

  const handleSave = async () => {
    if (!text.trim()) return;
    setSaving(true);
    const newRemark = appendRemark(entry.remark, text, airline, partnerName);
    const updated = { ...entry, remark: newRemark };
    await dbUpdateEntry(updated, date || "");
    onSave(updated);
    setSaving(false);
    onClose();
  };

  const lines = (entry.remark || "").split("\n").filter(Boolean);

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ ...overlayBackdrop, zIndex: 200,
      display: "flex", alignItems: isWide ? "center" : "flex-end",
      justifyContent: "center", padding: isWide ? 24 : 0 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...GLASS.modal,
        width: "100%", maxWidth: isWide ? 400 : "100%",
        borderRadius: isWide ? 16 : "16px 16px 0 0",
        maxHeight: "88vh", display: "flex", flexDirection: "column", ...dragStyle }}>

        {!isWide && (
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px",
                        flexShrink: 0, touchAction: "none", cursor: "grab" }} {...handleProps}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: SA.borderGray }} />
          </div>
        )}
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, letterSpacing: "0.18em",
              textTransform: "uppercase", marginBottom: 4 }}>
              Add Remark
            </div>
            <div style={{ fontSize: TYPOGRAPHY.sub, color: SA.lightSilver, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
              {entry.inFlt} {"->"} {entry.outFlt}
              {entry.dest ? <span style={{ color: SA.silver }}> {"\u00b7"} {entry.dest}</span> : null}
            </div>
          </div>
          <Button variant="icon" onClick={onClose}
            style={{ background: SA.medGray, color: SA.silver, width: 28, height: 28, fontSize: 16, flexShrink: 0 }}>×</Button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {/* Existing remarks */}
          {lines.length > 0 && (
            <div style={{ background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
              borderRadius: RADIUS.md, padding: "10px 14px", marginBottom: 16,
              maxHeight: 110, overflowY: "auto" }}>
              {lines.map((line, i) => {
                const match = line.match(REMARK_PATTERN);
                return (
                  <div key={line} style={{ marginBottom: i < lines.length - 1 ? 8 : 0 }}>
                    {match ? (
                      <span>
                        <span style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                          marginRight: 6 }}>[{match[1]} {match[2]}]</span>
                        <span style={{ fontSize: TYPOGRAPHY.label, color: SA.lightSilver }}>{match[3]}</span>
                      </span>
                    ) : (
                      <span style={{ fontSize: TYPOGRAPHY.label, color: SA.lightSilver }}>{line}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Remark input */}
          <div>
            <label style={{ display: "block", fontSize: TYPOGRAPHY.caption, color: SA.silver,
              letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
              Remark
            </label>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Type your remark..."
              rows={3}
              autoFocus
              onKeyDown={e => { if (e.key === "Enter" && e.metaKey && text.trim()) handleSave(); }}
              style={{ width: "100%", boxSizing: "border-box",
                background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
                borderRadius: RADIUS.md, color: SA.white,
                padding: "12px 14px", fontSize: TYPOGRAPHY.note,
                fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                outline: "none", resize: "none", lineHeight: 1.5 }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${SA.borderGray}` }}>
          <div style={{ display: "flex", gap: 10 }}>
            <Button variant="secondary" onClick={onClose}
              style={{ flex: 1, padding: "13px", fontSize: TYPOGRAPHY.caption,
                letterSpacing: "0.12em", textTransform: "uppercase",
                fontFamily: "'Helvetica Neue',Arial,sans-serif",
                borderRadius: RADIUS.md }}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!text.trim() || saving}
              style={{ flex: 2, padding: "13px",
                background: text.trim() && !saving ? "rgba(255,255,255,0.08)" : SA.darkGray,
                border: text.trim() && !saving ? `1px solid ${SA.lightSilver}` : `1px solid ${SA.borderGray}`,
                color: text.trim() && !saving ? SA.white : SA.silver,
                fontSize: TYPOGRAPHY.body, letterSpacing: "0.1em", textTransform: "uppercase",
                fontFamily: "'Helvetica Neue',Arial,sans-serif", fontWeight: 700,
                borderRadius: RADIUS.md, transition: "all 0.2s" }}>
              {saving ? "Saving..." : "Add Remark"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
